<template>
  <div
    class="container-fluid"
  >
    <b-modal
      v-model="showingWelcomeModal"
      title="Read this before you begin"
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      :hide-footer="true"
    >
      The purpose of this exercise is to identify your most productive lead sources so you can include
      them in your business plan.  On this screen, we show your production history since January 1.
      To identify your most productive lead sources, simply associate each transaction with its corresponding
      lead source.  Please don't worry if a few transactions are missing as long as there is enough data
      to give you a clear image of your top lead sources.  If you want to manually add additional transactions
      or change the production history from 12 months to a longer period, please click settings to modify.
      <hr>
      <div class="d-flex align-items-center justify-content-between">
        <b-checkbox
          v-model="dismissWelcomeModal"
          size="sm"
        >
          Don't show this again
        </b-checkbox>
        <button
          class="btn btn-sm btn-primary"
          @click="showingWelcomeModal = false"
        >
          <i class="fa fa-thumbs-up mr-1"></i>
          Got It
        </button>
      </div>
    </b-modal>

    <b-modal
      :visible="showSettingsModal"
      title="Prepwork Settings"
      hide-footer
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
    >
      <form
        :action="urls.businessPlan.changeClosingConnection(userId, year)"
        method="post"
        @submit="reconfiguring = true"
      >
        <input type="hidden" name="_token" v-model="csrf">

        <b-form-group
          label="Load my transactions from this source:"
        >
          <b-select
            name="connection"
            v-model="settings.closings_connection"
          >
            <b-form-select-option
              value="demo"
              v-if="canDemo"
            >
              Demo Data
            </b-form-select-option>
            <b-form-select-option value="darwin">
              My Accounting Profile
            </b-form-select-option>
            <b-form-select-option value="listings">
              The MLS
            </b-form-select-option>
          </b-select>
          <small class="form-text text-muted">
            If you joined our company recently, use the <b>MLS</b> option.
          </small>
        </b-form-group>

        <b-form-group
          label="Load this many years of transactions:"
        >
          <b-select
            name="years"
            v-model="settings.years"
           >
            <b-select-option :value="1">1 Year</b-select-option>
            <b-select-option :value="2">2 Years</b-select-option>
            <b-select-option :value="3">3 Years</b-select-option>
          </b-select>
        </b-form-group>

        <hr>
        <div class="d-flex">
          <button
            class="btn btn-white"
            @click.prevent="showSettingsModal = false"
            :disabled="reconfiguring"
            typ="button"
          >
            Cancel
          </button>
          <button
            class="btn btn-primary ml-auto"
            type="submit"
            :disabled="reconfiguring"
          >
            <i
              class="fa fa-sync mr-1"
            ></i>
            Reload Transactions
          </button>
        </div>
      </form>
    </b-modal>

    <b-modal
      :visible="showAddTransactionModal"
      title="Add a Transaction"
      hide-footer
      hide-header-close
      no-close-on-esc
      no-close-on-backdrop
    >
      <form
        :action="urls.businessPlan.addClosing(userId, year)"
        method="post"
        @submit="reconfiguring = true"
      >
        <input type="hidden" name="_token" v-model="csrf">

        <b-form-group
          label="The address of the closing:"
        >
          <b-input
            name="address"
          ></b-input>
        </b-form-group>

        <b-form-group
          label="The date of the closing:"
        >
          <b-input
            type="date"
            name="closing_date"
          ></b-input>
        </b-form-group>

        <b-form-group
          label="The price at closing:"
        >
          <b-input-group prepend="$" append=".00">
            <b-input
              type="number"
              name="closing_price"
            ></b-input>
          </b-input-group>
        </b-form-group>

        <b-form-group
          label="What side of the sale were you on?"
        >
          <b-select
            name="side"
            :value="'L'"
          >
            <b-select-option :value="'L'">My client was the seller</b-select-option>
            <b-select-option :value="'S'">My client was the buyer</b-select-option>
          </b-select>
          <small class="form-text text-muted">
            Choose the one that was the source of the lead.
          </small>
        </b-form-group>

        <b-form-group
          label="What was the source of this lead?"
        >
          <b-form-select
            name="lead_source"
            :value="null"
          >
            <b-form-select-option :value="null">Choose one</b-form-select-option>
            <b-form-select-option
              v-for="leadSource in leadSources"
              :key="`source_${leadSource}`"
              :value="leadSource"
            >
              {{ leadSource }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>

        <hr>
        <div class="d-flex">
          <button
            class="btn btn-white"
            type="button"
            @click.prevent="showAddTransactionModal = false"
            :disabled="reconfiguring"
          >
            Cancel
          </button>
          <button
            class="btn btn-primary ml-auto"
            type="submit"
            :disabled="reconfiguring"
          >
            Save Transaction
          </button>
        </div>
      </form>
    </b-modal>

    <div
      v-if="error"
      class="alert alert-warning"
    >
      <div>
        <b>Oops!</b>
        {{ error }}
        You can either <b>Add a Transaction</b> or <b>Change Settings</b>,
        or just skip this step.
      </div>
      <hr>
      <div class="d-flex">
        <button
          class="btn btn-warning"
          @click.prevent="showAddTransactionModal = true"
        >
          <i class="fa fa-plus"></i>
          Add a Transaction
        </button>
        <button
          class="btn btn-warning ml-2"
          @click.prevent="showSettingsModal = true"
        >
          <i class="fa fa-cog mr-1"></i>
          Settings
        </button>
        <a
          :href="urls.businessPlan.showEdit(userId, year)"
          class="btn btn-warning ml-auto"
        >
          Skip
          <i class="fa fa-arrow-right ml-1"></i>
        </a>
      </div>
    </div>

    <div
      v-if="loading"
      class="alert alert-info media align-items-center"
    >
      <div class="media-body">
        We are loading your closings. This can take a few minutes. Please wait...
      </div>
    </div>

    <div
      v-if="plan.id"
      class="row"
      sticky-container
    >
      <div
        class="col-12 col-md-8"
      >
        <div
          v-if="(transactions < 1 && plan.closings_connection !== 'listings'  && plan.closings_connection !== 'demo')"
          class="alert alert-warning"
        >
          <div>
            There are no closings in your accounting profile between {{ start }} and {{ end }}.
            That's OK. You can either <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <hr>
          <div class="d-flex">
            <button
              class="btn btn-warning"
              @click.prevent="showAddTransactionModal = true"
            >
              <i class="fa fa-plus"></i>
              Add a Transaction
            </button>
            <button
              class="btn btn-warning ml-2"
              @click.prevent="showSettingsModal = true"
            >
              <i class="fa fa-cog mr-1"></i>
              Settings
            </button>
          </div>
        </div>

        <div
          v-if="(transactions < 1 && plan.closings_connection !== 'darwin' && plan.closings_connection !== 'demo')"
          class="alert alert-warning"
        >
          <div>
            There are no closings for you in the MLS between {{ start }} and {{ end }}.
            That's OK. You can either <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <hr>
          <div class="d-flex">
            <button
              class="btn btn-warning"
              @click.prevent="showAddTransactionModal = true"
            >
              <i class="fa fa-plus"></i>
              Add a Transaction
            </button>
            <button
              class="btn btn-warning ml-2"
              @click.prevent="showSettingsModal = true"
            >
              <i class="fa fa-cog mr-1"></i>
              Settings
            </button>
          </div>
        </div>

        <div
          v-if="leadSourceHelp && transactions > 0"
          class="card"
        >
          <div class="card-header">
            <i class="fa fa-question-circle mr-1"></i>
            How do I know which <b>Lead Source</b> to choose?
          </div>
          <div class="card-body">
            <table class="table">
              <tr>
                <td>
                  SOI
                </td>
                <td>
                  Your "database"&mdash;past clients and other referral sources
                </td>
              </tr>
              <tr>
                <td>
                  Open House
                </td>
                <td>
                  Leads from Open Houses
                </td>
              </tr>
              <tr>
                <td>
                  Geo Farm
                </td>
                <td>
                  Leads from marketing directed to a specific neighborhood, building, etc
                </td>
              </tr>
              <tr>
                <td>
                  Online Leads
                </td>
                <td>
                  Leads from online sources
                </td>
              </tr>
              <tr>
                <td>
                  FSBO/ Expireds
                </td>
                <td>
                  Expired listing or For Sale By Owner (FSBO) leads
                </td>
              </tr>
              <tr>
                <td>
                  Agent-To-Agent
                </td>
                <td>
                  Referral received from another agent (you were primary agent)
                </td>
              </tr>
              <tr>
                <td>
                  Assist/ Alt Agent/ Referral Fee
                </td>
                <td>
                  Not my transaction! This designation is for deals in which
                  you received a fee, but were not the primary agent
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div
          v-if="transactions > 0"
          class="alert alert-warning"
        >
          Showing transactions between {{ start }} and {{ end }}.
        </div>

        <section
          v-for="(group, key) in groups"
          v-if="transactions > 0 && (group.transactions.personal || group.transactions.company)"
          class="my-5"
        >
          <header class="mb-4">
            <h4 class="h4 font-weight-bold text-gold">
              {{ group.name }}
            </h4>
          </header>
          <section v-for="(source, sourceName) in group.transactions">
            <header>
              <h5 class="h6 mb-3 font-weight-bold text-uppercase">
                <span v-if="sourceName === 'personal'">Personal {{ group.name }}</span>
                <span v-if="sourceName === 'company'">Company {{ group.name }}</span>
              </h5>
            </header>
            <table class="table">
              <thead>
                <tr class="bg-dark text-white">
                  <th class="font-weight-normal">
                    Client&nbsp;Name
                  </th>
                  <th class="font-weight-normal">
                    Listing
                  </th>
                  <th class="font-weight-normal">
                    Lead&nbsp;Source
                    <a href="#" class="text-white ml-1" @click.prevent="showLeadSourceHelp()">
                      <i class="fa fa-question-circle"></i>
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transaction in source">
                  <td>
                    <div v-if="!hideSensitiveDetails">
                      <div v-if="key === 'listing' && transaction.sellers">
                        {{ transaction.sellers }}
                      </div>
                      <div v-else-if="key === 'buyer' && transaction.buyers">
                        {{ transaction.buyers }}
                      </div>
                      <div v-else>
                        <div>Unknown</div>
                      </div>
                    </div>
                    <div v-if="hideSensitiveDetails">
                      <span class="text-muted">
                        Client Name Hidden
                      </span>
                    </div>
                    <div v-if="transaction.closings_connection === 'custom'">
                      <form
                        :action="urls.businessPlan.deleteClosing(userId, transaction.closings_connection, transaction.closings_id, year)"
                        method="post"
                      >
                        <input type="hidden" name="_token" v-model="csrf">
                        <confirm
                          message="Are you sure you want to remove this transaction from your prepwork?"
                          class="btn btn-link btn-sm p-0 mt-2"
                        >
                          Delete
                        </confirm>
                      </form>
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-bold" v-if="!hideSensitiveDetails">
                      {{ transaction.address }}
                    </div>
                    <div class="text-muted" v-if="hideSensitiveDetails">
                      Address Hidden
                    </div>
                    <div>{{ formatCurrency(transaction.closing_price) }}</div>
                    <small>{{ ago(transaction.closing_date) }}</small>
                  </td>
                  <td style="width:200px">
                    <select
                      class="custom-select"
                      @change="setClosingLeadSource(transaction, $event)"
                    >
                      <option value="">Choose one</option>
                      <option
                        v-for="leadSource in group.leadSources"
                        :selected="leadSource === transaction.lead_source"
                      >
                        {{ leadSource }}
                      </option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <div
          v-if="transactions > 0 && plan.closings_connection !== 'listings' && plan.closings_connection !== 'demo'"
          class="alert alert-warning"
        >
          <div>
            These are closings from your accounting profile. Is something missing?
            That's OK. You can either <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <hr>
          <div class="d-flex">
            <button
              class="btn btn-warning"
              @click.prevent="showAddTransactionModal = true"
            >
              <i class="fa fa-plus"></i>
              Add a Transaction
            </button>
            <button
              class="btn btn-warning ml-2"
              @click.prevent="showSettingsModal = true"
            >
              <i class="fa fa-cog mr-1"></i>
              Settings
            </button>
          </div>
        </div>

        <div
          v-if="transactions > 0 && plan.closings_connection === 'demo'"
          class="alert alert-warning"
        >
          <div>
            These are demo transactions.
            You can <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <hr>
          <div class="d-flex">
            <button
              class="btn btn-warning"
              @click.prevent="showAddTransactionModal = true"
            >
              <i class="fa fa-plus"></i>
              Add a Transaction
            </button>
            <button
              class="btn btn-warning ml-2"
              @click.prevent="showSettingsModal = true"
            >
              <i class="fa fa-cog mr-1"></i>
              Settings
            </button>
          </div>
        </div>

        <div
          v-if="transactions > 0 && plan.closings_connection !== 'darwin' && plan.closings_connection !== 'demo'"
          class="alert alert-warning"
        >
          <div
            v-if="plan.closings_connection === 'demo'"
          >
            These closings are fake data created for demonstration purposes.
            You can either <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <div v-else>
            These are your closings from the MLS. Is something missing?
            That's OK. You can either <b>Add a Transaction</b> or <b>Change Settings</b>.
          </div>
          <hr>
          <div class="d-flex">
            <button
              class="btn btn-warning"
              @click.prevent="showAddTransactionModal = true"
            >
              <i class="fa fa-plus"></i>
              Add a Transaction
            </button>
            <button
              class="btn btn-warning ml-2"
              @click.prevent="showSettingsModal = true"
            >
              <i class="fa fa-cog mr-1"></i>
              Settings
            </button>
          </div>
        </div>

      </div>

      <div
        class="col-12 col-md-4"
      >
        <div
          v-if="plan.id"
          class="pt-4"
          v-sticky
        >
          <div class="card">
            <div class="card-body">
              <div class="h1 mb-4">
                <i class="fa fa-calendar"></i>
              </div>
              <h3 class="h5 font-weight-bold mb-4">
                Explore your transaction history.
              </h3>
              <p>
                Please identify the <b>Lead Source</b> for each transaction, and your
                changes will be saved automatically. When you're done, click <b>Done</b>.
              </p>
              <div>
                <small class="d-block mb-2">{{ transactionsReviewed }} of {{ transactions }} transactions reviewed</small>
                <b-progress
                  :value="transactionsReviewed"
                  :max="transactions"
                  :animated="transactions - transactionsReviewed > 0"
                  :variant="transactions - transactionsReviewed > 0 ? '' : 'success'"
                ></b-progress>
              </div>
              <div class="pt-3">
                <a
                  v-if="!leadSourceHelp"
                  href="#"
                  class="alert alert-info d-block text-body mb-0 text-decoration-none"
                  @click.prevent="showLeadSourceHelp()"
                >
                  <i class="fa fa-question-circle mr-1"></i>
                  How do I know which <b>Lead Source</b> to choose?
                  <span class="text-info text-underline">
                    Click here
                  </span>
                </a>
              </div>
            </div>
            <div class="card-footer d-flex">
              <button
                class="btn btn-white"
                @click.prevent="showSettingsModal = true"
              >
                <i class="fa fa-cog mr-1"></i>
                Settings
              </button>
              <button
                class="btn btn-primary ml-auto"
                @click="approve()"
                :disabled="approving"
              >
                {{ approving ? 'Saving...' : 'Done' }}
                <i class="fa fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import $ from 'jquery'
import { each, filter, sum, map } from 'lodash'
import { getBusinessPlanPrepwork, approveBusinessPlanLtm, setClosingLeadSource, dismissPrepworkWelcomeModal } from '@/api'
import Confirm from '@/components/Confirm'
import numeral from 'numeral'
import moment from 'moment'
import wait from '@/lib/wait'
import urls from '@/api/urls'
import axios from 'axios'
import { MessageBag } from '@/lib/MessageBag'

export default {
  components: {
    Confirm,
  },
  props: {
    userId: {
      type: String,
      required: true
    },
    year: {
      type: [String, Number],
      required: true
    },
    hideSensitiveDetails: {
      type: Boolean,
      default: false
    },
    leadSources: {
      type: Array,
      required: true
    },
    oc: {
      type: Object,
      default () {
        return {
          email: ''
        }
      }
    },
    showWelcomeModal: {
      type: Boolean,
      default: false
    },
    canDemo: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    range () {
      if (this.dates.start && this.dates.end) {
        return moment(this.dates.start).format('MMMM Do YYYY') + '-' + moment(this.dates.end).format('YYYY')
      } else {
        return null
      }
    },
    start () {
      return this.dates.start ? moment(this.dates.start).utc().format('MMMM D, YYYY') : ''
    },
    end () {
      return this.dates.end ? moment(this.dates.end).utc().format('MMMM D, YYYY') : ''
    },
    transactionsReviewed () {
      return sum(map(this.groups, (group) => {
        return sum(map(group.transactions, (transactions, sourceName) => {
          return filter(transactions, (transaction) => {
            return sourceName !== 'personal' || !!transaction.lead_source
          }).length
        }))
      }))
    },
    csrf () {
      return axios.defaults.headers.common['X-CSRF-TOKEN']
    }
  },
  data () {
    return {
      urls,
      reconfiguring: false,
      error: null,
      loading: null,
      plan: {
        id: null,
        approved_ltm_at: null,
        closings_load_status: null,
        closings_connection: null
      },
      dates: {
        start: null,
        end: null
      },
      approving: false,
      transactions: null,
      leadSourceHelp: false,
      groups: {
        listing: {
          name: 'Listing Sides',
          leadSources: this.leadSources,
          transactions: {}
        },
        buyer: {
          name: 'Buyer Sides',
          leadSources: this.leadSources,
          transactions: {}
        },
        rental: {
          name: 'Rental Sides',
          leadSources: this.leadSources,
          transactions: {}
        },
        other: {
          name: 'Referrals and Other Income',
          leadSources: this.leadSources,
          transactions: {}
        }
      },
      showingWelcomeModal: this.showWelcomeModal,
      dismissWelcomeModal: false,
      showSettingsModal: false,
      settings: {
        closings_connection: 'darwin',
        years: 1,
      },
      showAddTransactionModal: false,
    }
  },
  watch: {
    dismissWelcomeModal(value) {
      dismissPrepworkWelcomeModal(value, this.userId)
    }
  },
  mounted () {
    this.loadPrepwork()
  },
  methods: {
    showHelp () {
      $(window).trigger('help.requested')
    },
    async loadPrepwork () {
      wait.loading()
      try {
        let response = await getBusinessPlanPrepwork(this.userId, this.year)
        this.loading = false
        this.plan = response.data.plan
        this.settings.closings_connection = this.plan.closings_connection || 'darwin'
        this.dates = response.data.dates
        this.transactions = response.data.count
        each(response.data.transactions, (transactions, key) => {
          this.groups[key].transactions = transactions
        })
        wait.loaded()
      } catch (e) {
        if (e.response) {
          if (e.response.status === 422) {
            this.loading = true
            setTimeout(this.loadPrepwork, 5000)
          } else {
            this.loading = false
            this.error = e.response.data.message
            wait.oops()
          }
        } else {
          console.error(e)
        }
      }
    },
    formatCurrency (amount) {
      return numeral(amount).format('$0,0')
    },
    ago (date) {
      return moment(date).fromNow()
    },
    approve () {
      this.approving = true
      wait.saving()
      approveBusinessPlanLtm(this.userId, this.year)
        .then((response) => {
          wait.saved()
          this.plan = response.data
          document.location = urls.businessPlan.showEdit(this.userId, this.year)
        })
        .catch((response) => {
          wait.oops()
          this.approving = false
        })
    },
    setClosingLeadSource (transaction, event) {
      let leadSource = $(event.target).val()
      wait.saving()
      setClosingLeadSource(transaction.id, leadSource)
        .then((response) => {
          transaction.lead_source = leadSource
          wait.saved()
        })
        .catch((response) => {
          wait.oops()
        })
    },
    showLeadSourceHelp () {
      this.leadSourceHelp = true
      $('html, body').animate({ scrollTop: 0 })
    },
  }
}
</script>